/**
 * NRC Operating Reactor Status — live daily feed from Nuclear Regulatory Commission.
 * Free, no API key, pipe-delimited text format.
 *
 * Daily feed observations are joined to the existing static location catalog.
 * Used to: identify nearby catalog reactor sites, assess regional nuclear infrastructure maturity.
 *
 * Source: https://www.nrc.gov/reading-rm/doc-collections/event-status/reactor-status/
 */
import { parseReactorStatus, summarizeNrcDailyPower, type NrcDailyPowerSummary } from '../../lib/nrcReactorStatus';
import type { DataProvider, ProviderGeoContext, ProviderResult } from './types';
import { gridCellKey, haversineKm } from './types';
import { NRC_FEED_TTL_MS, NRC_NATIONAL_FEED_URL, nrcReceiptIsFresh, type NrcNationalFeed } from '../../lib/nrcNationalFeed';
import { loadNrcNationalFeed } from '../refdata/nrcNationalFeedClient';

export interface NrcReactorData {
  nearestReactorName: string;
  nearestReactorDistKm: number;
  /**
   * @deprecated MISLEADING NAME — this is a legacy daily power percentage alias,
   * NOT MW nameplate. Kept for back-compat with reports written before R-29.
   * New readers should use `dailyPower` and
   * `nearestReactorNameplateMw` instead.
   */
  nearestReactorPower: number;
  /** @deprecated Daily power alias, not annual capacity factor. -1 = unknown/partial. */
  nearestReactorCapacityFactorPct: number;
  dailyPower: NrcDailyPowerSummary;
  powerStatusFeed: { latestReportDate: string | null; invalidRows: number; unmappedUnits: string[]; provenance?: NrcNationalFeed['provenance'] };
  /** Site-aggregated static catalog nameplate capacity in MW (sum across units at the same site).
   *  Sourced from REACTOR_LOCATIONS static table (NRC Information Digest / EIA Form 860).
   *  0 = unknown. */
  nearestReactorNameplateMw: number;
  reactorsWithin200km: number;
  reactorList: {
    name: string;
    distKm: number;
    /** @deprecated Daily power alias, not capacity factor or MW. */
    power: number;
    /** @deprecated Daily power alias, -1 = unknown/partial. */
    capacityFactorPct: number;
    dailyPower: NrcDailyPowerSummary;
    /** Site-aggregated static catalog nameplate MW. */
    nameplateMw: number;
    /** Number of units aggregated at this site. */
    units: number;
  }[];
  nuclearInfraMaturity: 'High' | 'Moderate' | 'Low';
}

/**
 * Static reactor location catalog (2025 basis; not a verified complete current fleet).
 * Coordinates from NRC Information Digest (NUREG-1350).
 * `nameplateMw` retains legacy per-unit catalog ratings attributed to NRC Information Digest /
 *  EIA Form860; it is independent of the daily status feed and has not been refreshed here.
 * Used as fallback when live feed is unavailable, and for distance calculations.
 */
const REACTOR_LOCATIONS: Record<string, { lat: number; lng: number; nameplateMw: number }> = {
  'Arkansas Nuclear 1':           { lat: 35.310, lng: -93.230,  nameplateMw: 836 },
  'Arkansas Nuclear 2':           { lat: 35.310, lng: -93.230,  nameplateMw: 995 },
  'Beaver Valley 1':              { lat: 40.622, lng: -80.432,  nameplateMw: 921 },
  'Beaver Valley 2':              { lat: 40.622, lng: -80.432,  nameplateMw: 933 },
  'Braidwood 1':                  { lat: 41.243, lng: -88.227,  nameplateMw: 1185 },
  'Braidwood 2':                  { lat: 41.243, lng: -88.227,  nameplateMw: 1183 },
  'Browns Ferry 1':               { lat: 34.704, lng: -87.119,  nameplateMw: 1102 },
  'Browns Ferry 2':               { lat: 34.704, lng: -87.119,  nameplateMw: 1115 },
  'Browns Ferry 3':               { lat: 34.704, lng: -87.119,  nameplateMw: 1115 },
  'Brunswick 1':                  { lat: 33.958, lng: -78.010,  nameplateMw: 932 },
  'Brunswick 2':                  { lat: 33.958, lng: -78.010,  nameplateMw: 932 },
  'Byron 1':                      { lat: 42.075, lng: -89.282,  nameplateMw: 1164 },
  'Byron 2':                      { lat: 42.075, lng: -89.282,  nameplateMw: 1136 },
  'Callaway 1':                   { lat: 38.762, lng: -91.781,  nameplateMw: 1190 },
  'Calvert Cliffs 1':             { lat: 38.435, lng: -76.443,  nameplateMw: 865 },
  'Calvert Cliffs 2':             { lat: 38.435, lng: -76.443,  nameplateMw: 862 },
  'Catawba 1':                    { lat: 35.050, lng: -81.070,  nameplateMw: 1135 },
  'Catawba 2':                    { lat: 35.050, lng: -81.070,  nameplateMw: 1135 },
  'Clinton 1':                    { lat: 40.172, lng: -88.834,  nameplateMw: 1066 },
  'Columbia Generating Station':  { lat: 46.471, lng: -119.333, nameplateMw: 1190 },
  'Comanche Peak 1':              { lat: 32.299, lng: -97.786,  nameplateMw: 1218 },
  'Comanche Peak 2':              { lat: 32.299, lng: -97.786,  nameplateMw: 1207 },
  'Cooper':                       { lat: 40.362, lng: -95.641,  nameplateMw: 770 },
  'Davis-Besse 1':                { lat: 41.597, lng: -83.086,  nameplateMw: 894 },
  'Diablo Canyon 1':              { lat: 35.211, lng: -120.854, nameplateMw: 1138 },
  'Diablo Canyon 2':              { lat: 35.211, lng: -120.854, nameplateMw: 1118 },
  'Dresden 2':                    { lat: 41.390, lng: -88.271,  nameplateMw: 894 },
  'Dresden 3':                    { lat: 41.390, lng: -88.271,  nameplateMw: 879 },
  'Farley 1':                     { lat: 31.223, lng: -85.109,  nameplateMw: 888 },
  'Farley 2':                     { lat: 31.223, lng: -85.109,  nameplateMw: 887 },
  'Fermi 2':                      { lat: 41.963, lng: -83.258,  nameplateMw: 1126 },
  'Grand Gulf 1':                 { lat: 32.008, lng: -91.047,  nameplateMw: 1500 }, // ~1419 net / ~1500 gross — largest single-unit BWR in US
  'Hatch 1':                      { lat: 31.934, lng: -82.344,  nameplateMw: 876 },
  'Hatch 2':                      { lat: 31.934, lng: -82.344,  nameplateMw: 883 },
  'Hope Creek 1':                 { lat: 39.468, lng: -75.535,  nameplateMw: 1180 },
  'LaSalle 1':                    { lat: 41.244, lng: -88.670,  nameplateMw: 1118 },
  'LaSalle 2':                    { lat: 41.244, lng: -88.670,  nameplateMw: 1120 },
  'Limerick 1':                   { lat: 40.224, lng: -75.587,  nameplateMw: 1130 },
  'Limerick 2':                   { lat: 40.224, lng: -75.587,  nameplateMw: 1134 },
  'McGuire 1':                    { lat: 35.432, lng: -80.949,  nameplateMw: 1158 },
  'McGuire 2':                    { lat: 35.432, lng: -80.949,  nameplateMw: 1158 },
  'Millstone 2':                  { lat: 41.309, lng: -72.169,  nameplateMw: 870 },
  'Millstone 3':                  { lat: 41.309, lng: -72.169,  nameplateMw: 1233 },
  'Monticello':                   { lat: 45.333, lng: -93.849,  nameplateMw: 671 },
  'Nine Mile Point 1':            { lat: 43.522, lng: -76.410,  nameplateMw: 620 },
  'Nine Mile Point 2':            { lat: 43.522, lng: -76.410,  nameplateMw: 1227 },
  'North Anna 1':                 { lat: 38.061, lng: -77.790,  nameplateMw: 948 },
  'North Anna 2':                 { lat: 38.061, lng: -77.790,  nameplateMw: 944 },
  'Oconee 1':                     { lat: 34.792, lng: -82.899,  nameplateMw: 847 },
  'Oconee 2':                     { lat: 34.792, lng: -82.899,  nameplateMw: 848 },
  'Oconee 3':                     { lat: 34.792, lng: -82.899,  nameplateMw: 859 },
  'Palisades':                    { lat: 42.322, lng: -86.315,  nameplateMw: 805 }, // Shut down May 2022 — kept for back-compat; restart in progress under Holtec
  'Palo Verde 1':                 { lat: 33.388, lng: -112.862, nameplateMw: 1311 },
  'Palo Verde 2':                 { lat: 33.388, lng: -112.862, nameplateMw: 1314 },
  'Palo Verde 3':                 { lat: 33.388, lng: -112.862, nameplateMw: 1312 },
  'Peach Bottom 2':               { lat: 39.759, lng: -76.269,  nameplateMw: 1305 },
  'Peach Bottom 3':               { lat: 39.759, lng: -76.269,  nameplateMw: 1305 },
  'Perry 1':                      { lat: 41.801, lng: -81.144,  nameplateMw: 1240 },
  'Point Beach 1':                { lat: 44.281, lng: -87.537,  nameplateMw: 640 },
  'Point Beach 2':                { lat: 44.281, lng: -87.537,  nameplateMw: 640 },
  'Prairie Island 1':             { lat: 44.622, lng: -92.633,  nameplateMw: 530 },
  'Prairie Island 2':             { lat: 44.622, lng: -92.633,  nameplateMw: 531 },
  'Quad Cities 1':                { lat: 41.726, lng: -90.340,  nameplateMw: 908 },
  'Quad Cities 2':                { lat: 41.726, lng: -90.340,  nameplateMw: 911 },
  'River Bend 1':                 { lat: 30.757, lng: -91.332,  nameplateMw: 967 },
  'Robinson 2':                   { lat: 34.403, lng: -80.159,  nameplateMw: 741 },
  'Salem 1':                      { lat: 39.463, lng: -75.536,  nameplateMw: 1170 },
  'Salem 2':                      { lat: 39.463, lng: -75.536,  nameplateMw: 1158 },
  'Seabrook 1':                   { lat: 42.898, lng: -70.849,  nameplateMw: 1244 },
  'Sequoyah 1':                   { lat: 35.224, lng: -85.089,  nameplateMw: 1152 },
  'Sequoyah 2':                   { lat: 35.224, lng: -85.089,  nameplateMw: 1126 },
  'Shearon Harris 1':             { lat: 35.633, lng: -78.956,  nameplateMw: 932 },
  'South Texas 1':                { lat: 28.795, lng: -96.048,  nameplateMw: 1280 },
  'South Texas 2':                { lat: 28.795, lng: -96.048,  nameplateMw: 1280 },
  'St. Lucie 1':                  { lat: 27.349, lng: -80.246,  nameplateMw: 981 },
  'St. Lucie 2':                  { lat: 27.349, lng: -80.246,  nameplateMw: 987 },
  'Surry 1':                      { lat: 37.166, lng: -76.698,  nameplateMw: 838 },
  'Surry 2':                      { lat: 37.166, lng: -76.698,  nameplateMw: 838 },
  'Susquehanna 1':                { lat: 41.091, lng: -76.147,  nameplateMw: 1265 },
  'Susquehanna 2':                { lat: 41.091, lng: -76.147,  nameplateMw: 1259 },
  'Turkey Point 3':               { lat: 25.435, lng: -80.331,  nameplateMw: 837 },
  'Turkey Point 4':               { lat: 25.435, lng: -80.331,  nameplateMw: 837 },
  'Vogtle 1':                     { lat: 33.142, lng: -81.763,  nameplateMw: 1215 },
  'Vogtle 2':                     { lat: 33.142, lng: -81.763,  nameplateMw: 1218 },
  'Vogtle 3':                     { lat: 33.142, lng: -81.763,  nameplateMw: 1117 }, // AP1000 — COD 2023
  'Vogtle 4':                     { lat: 33.142, lng: -81.763,  nameplateMw: 1117 }, // AP1000 — COD 2024
  'Waterford 3':                  { lat: 29.995, lng: -90.472,  nameplateMw: 1218 },
  'Watts Bar 1':                  { lat: 35.605, lng: -84.793,  nameplateMw: 1167 },
  'Watts Bar 2':                  { lat: 35.605, lng: -84.793,  nameplateMw: 1167 },
  'Wolf Creek 1':                 { lat: 38.239, lng: -95.689,  nameplateMw: 1200 },
};

/** Explicit feed aliases verified against the NRC source; no fuzzy cross-unit matching. */
const NRC_UNIT_ALIASES: Record<string, string> = {
  'Callaway': 'Callaway 1', 'Clinton': 'Clinton 1', 'Davis-Besse': 'Davis-Besse 1',
  'Harris 1': 'Shearon Harris 1', 'River Bend Station 1': 'River Bend 1',
  'Saint Lucie 1': 'St. Lucie 1', 'Saint Lucie 2': 'St. Lucie 2',
};

export const nrcReactorProvider: DataProvider<NrcReactorData> = {
  id: 'nrc-reactors',
  name: 'NRC Daily Power Status / Static Reactor Locations',
  cacheTtlMs: NRC_FEED_TTL_MS,
  // Old entries used undated annual-CF aliases and a longer cache lifetime.
  cacheKey: ({ lat, lng }) => `daily-status-national-v1_${gridCellKey(lat, lng)}`,
  requiresKey: false,

  async fetch(lat, lng, _apiKey?: string, geo?: ProviderGeoContext): Promise<ProviderResult<NrcReactorData>> {
    // CA3: `(lat, lng)` is the boundary centroid when a polygon is drawn
    // (`nrc-reactors` is in CENTROID_QUERY_PROVIDER_IDS), else the geocoded
    // address. The nearest-reactor distance below is haversine'd from this
    // point — it's a distance from the parcel, so the centroid is correct;
    // it also pins which 10-mile EPZ the site falls inside when it's near a
    // boundary.
    // Only the verified national cache supplies daily observations. A failed
    // refresh must not silently fall back to old source bytes or a local fetch.
    let snapshot = parseReactorStatus('');
    let provenance: NrcNationalFeed['provenance'] | undefined;
    try {
      const feed = await loadNrcNationalFeed(geo?.signal);
      geo?.signal?.throwIfAborted();
      if (!nrcReceiptIsFresh(feed.provenance)) throw new Error('NRC source receipt expired');
      snapshot = parseReactorStatus(feed.text, unit => NRC_UNIT_ALIASES[unit] ?? unit);
      provenance = feed.provenance;
    } catch {
      geo?.signal?.throwIfAborted();
      // Static proximity survives a daily feed failure; power stays unknown.
    }

    // Existing static catalog supplies proximity and rating, never a live-power default.
    const siteMap = new Map<string, {
      distKm: number;
      units: number;
      unitNames: string[];
      totalNameplateMw: number;
    }>();

    for (const [unit, coords] of Object.entries(REACTOR_LOCATIONS)) {
      const siteName = unit.replace(/ \d+$/, ''); // "Vogtle 1" → "Vogtle"
      const dist = haversineKm(lat, lng, coords.lat, coords.lng);
      if (dist > 200) continue;

      const existing = siteMap.get(siteName);
      if (existing) {
        existing.units++;
        existing.totalNameplateMw += coords.nameplateMw;
        existing.unitNames.push(unit);
      } else {
        siteMap.set(siteName, {
          distKm: parseFloat(dist.toFixed(1)),
          units: 1,
          unitNames: [unit],
          totalNameplateMw: coords.nameplateMw,
        });
      }
    }

    const nearby: NrcReactorData['reactorList'] = [];
    for (const [siteName, info] of siteMap) {
      const dailyPower = summarizeNrcDailyPower(info.unitNames, snapshot);
      const legacyPower = dailyPower.powerPct ?? -1;
      nearby.push({
        name: `${siteName}${info.units > 1 ? ` (${info.units} units)` : ''}`,
        distKm: info.distKm,
        // Deprecated numeric keys remain for stored readers; partial/unknown is -1.
        power: legacyPower,
        capacityFactorPct: legacyPower,
        dailyPower,
        nameplateMw: info.totalNameplateMw,
        units: info.units,
      });
    }

    nearby.sort((a, b) => a.distKm - b.distKm);

    const nearest = nearby[0];
    const maturity: NrcReactorData['nuclearInfraMaturity'] =
      nearby.length >= 3 ? 'High' : nearby.length >= 1 ? 'Moderate' : 'Low';

    const data: NrcReactorData = {
      nearestReactorName: nearest?.name ?? 'None within 200km',
      nearestReactorDistKm: nearest?.distKm ?? 999,
      // Back-compat field retains the percentage semantics; populated for legacy readers.
      nearestReactorPower: nearest?.capacityFactorPct ?? -1,
      // Deprecated R-29 alias remains alongside canonical daily observation.
      nearestReactorCapacityFactorPct: nearest?.capacityFactorPct ?? -1,
      dailyPower: nearest?.dailyPower ?? summarizeNrcDailyPower([], snapshot),
      powerStatusFeed: { latestReportDate: snapshot.latestReportDate, invalidRows: snapshot.invalidRows,
        unmappedUnits: [...snapshot.units.keys()].filter(unit => !Object.hasOwn(REACTOR_LOCATIONS, unit)).sort(),
        ...(provenance ? { provenance } : {}) },
      nearestReactorNameplateMw: nearest?.nameplateMw ?? 0,
      reactorsWithin200km: nearby.length,
      reactorList: nearby.slice(0, 5), // top 5 nearest
      nuclearInfraMaturity: maturity,
    };

    return {
      data,
      // Never extend an older national receipt by another site-provider TTL.
      // Unavailable results are not cached, so the next run can retry recovery.
      cacheTtlMs: provenance ? Math.max(0, Math.min(NRC_FEED_TTL_MS,
        Date.parse(provenance.retrievedAt) + NRC_FEED_TTL_MS - Date.now())) : 0,
      sources: [{
        field: 'nuclear_infrastructure',
        source: `NRC daily power status${snapshot.latestReportDate ? ` dated ${snapshot.latestReportDate}` : ' unavailable'}; static 2025 location catalog — ${nearby.length} catalog sites within 200km. ` +
          `Daily power is not annual capacity factor. Proximity/catalog ratings do not establish a complete current fleet, operating status or site power commitment. ` +
          `${data.powerStatusFeed.unmappedUnits.length} feed units lack static locations; ${snapshot.invalidRows} invalid feed rows.`,
        url: NRC_NATIONAL_FEED_URL,
        retrievedAt: provenance?.retrievedAt ?? new Date().toISOString(),
        ...(snapshot.latestReportDate ? { dataAsOf: snapshot.latestReportDate } : {}),
        ...(provenance ? { subItems: [{ topic: 'Immutable national feed',
          detail: `${provenance.object.path}#${provenance.object.generation}; SHA-256 ${provenance.object.sha256}; ` +
            `${provenance.sourceRows} returned rows, ${provenance.sourceUnits} named units, ${provenance.sourceDates} report dates ` +
            `(${provenance.earliestDate}–${provenance.latestDate}). Row accounting does not establish a complete operating fleet.` }] } : {}),
      }],
    };
  },
};
